/**
 * Peer Groups Page
 * 
 * Manage SIP peer groups for load balancing and routing.
 * Follows the established pattern from CertificatesPage and SipConfigurationPage.
 * Uses common components for consistent UI.
 */

import React, { useState, useMemo, useEffect } from 'react';
import {
  Users,
  Plus,
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  Eye,
  Search,
  Globe,
  Phone,
  Building2,
  X,
  XCircle,
  GitBranch,
  Scale,
  Activity,
  UserPlus,
  UserMinus,
  Edit3,
  Save
} from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { PeerGroupEditor } from '../../components/peer-group/PeerGroupEditor';
import {
  usePeerGroups,
  useDeletePeerGroup,
  useTogglePeerGroup,
  useAvailablePeers,
  useAddPeerToGroup,
  useRemovePeerFromGroup,
  useUpdatePeerMembership,
  type PeerMemberInfo,
  type PeerGroupWithPeers
} from '../../hooks/usePeerGroups';
import { useViewMode } from '../../hooks/useViewMode';
import {
  PageHeader,
  StatsGrid,
  StatCard,
  FilterBar,
  ItemCard,
  ItemCardIcon,
  ItemActions,
  EmptyState,
  LoadingState,
  ErrorState,
  DeleteConfirmModal,
  StatusBadge,
  enabledToStatus,
} from '../../components/common';

// ========================================
// CONSTANTS
// ========================================

const GROUP_TYPE_CONFIG = {
  peer_group: { label: 'Peer Group', icon: Users, color: 'bg-gray-100 text-gray-800' },
  trunk_group: { label: 'Trunk Group', icon: GitBranch, color: 'bg-purple-100 text-purple-800' },
  carrier_group: { label: 'Carrier', icon: Phone, color: 'bg-green-100 text-green-800' },
  teams_group: { label: 'Teams', icon: Building2, color: 'bg-blue-100 text-blue-800' },
  emergency_group: { label: 'Emergency', icon: AlertTriangle, color: 'bg-red-100 text-red-800' }
};

const STRATEGY_LABELS: Record<string, string> = {
  round_robin: 'Round Robin',
  weighted: 'Weighted',
  least_calls: 'Least Calls'
};

const SELECTION_MODE_LABELS: Record<string, string> = {
  failover: 'Failover',
  parallel: 'Parallel Fork'
};

const PEER_TYPE_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  teams: Building2,
  carrier: Phone,
  webrtc: Activity,
  softphone: Users,
  pbx: Globe
};

// ========================================
// TYPES
// ========================================

type FilterType = 'all' | 'enabled' | 'disabled' | 'peer_group' | 'trunk_group' | 'carrier_group' | 'teams_group' | 'emergency_group';

// ========================================
// MAIN COMPONENT
// ========================================

export default function PeerGroupsPage() {
  const queryClient = useQueryClient();

  // State management - use user preference for default view mode
  const [viewMode, setViewMode] = useViewMode();
  const [filterType, setFilterType] = useState<FilterType>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAddPeersModal, setShowAddPeersModal] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<PeerGroupWithPeers | null>(null);
  const [groupToDelete, setGroupToDelete] = useState<PeerGroupWithPeers | null>(null);
  const [editingGroup, setEditingGroup] = useState<PeerGroupWithPeers | null>(null);

  // API queries
  const { data: response, isLoading, error, refetch, isFetching } = usePeerGroups();
  const groups = useMemo(() => response?.data || [], [response]);

  // Mutations
  const deleteMutation = useDeletePeerGroup();
  const toggleMutation = useTogglePeerGroup();

  // Utility functions
  const getTypeConfig = (groupType: keyof typeof GROUP_TYPE_CONFIG) => {
    return GROUP_TYPE_CONFIG[groupType] || GROUP_TYPE_CONFIG.peer_group;
  };

  const handleDeleteGroup = (group: PeerGroupWithPeers) => {
    setGroupToDelete(group);
    setShowDeleteModal(true);
  };

  const confirmDeleteGroup = () => {
    if (groupToDelete) {
      deleteMutation.mutate(groupToDelete.id, {
        onSuccess: () => {
          setShowDeleteModal(false);
          setGroupToDelete(null);
        }
      });
    }
  };

  const handleEditGroup = (group: PeerGroupWithPeers) => {
    setEditingGroup(group);
    setShowCreateModal(true);
  };

  const handleViewDetails = (group: PeerGroupWithPeers) => {
    setSelectedGroup(group);
    setShowDetailsModal(true);
  };

  const handleAddPeers = (group: PeerGroupWithPeers) => {
    setSelectedGroup(group);
    setShowAddPeersModal(true);
  };

  const handleToggleEnabled = (group: PeerGroupWithPeers) => {
    toggleMutation.mutate({ groupId: group.id, enabled: !group.enabled });
  };

  // Filter and search logic
  const filteredGroups = useMemo(() => {
    return groups.filter(group => {
      // Type filter
      let matchesType = false;
      if (filterType === 'all') {
        matchesType = true;
      } else if (filterType === 'enabled') {
        matchesType = group.enabled === true;
      } else if (filterType === 'disabled') {
        matchesType = group.enabled === false;
      } else {
        matchesType = group.groupType === filterType;
      }

      // Search filter
      const matchesSearch = searchQuery === '' ||
        group.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (group.description?.toLowerCase().includes(searchQuery.toLowerCase()) ?? false);

      return matchesType && matchesSearch;
    });
  }, [groups, filterType, searchQuery]);

  // Sort groups by name
  const sortedGroups = [...filteredGroups].sort((a, b) => a.name.localeCompare(b.name));

  // Calculate statistics
  const stats = {
    total: groups.length,
    enabled: groups.filter(g => g.enabled).length,
    totalPeers: groups.reduce((sum, g) => sum + g.peerCount, 0),
    carrierGroups: groups.filter(g => g.groupType === 'carrier_group').length
  };

  // Filter options for FilterBar
  const filterOptions = [
    { value: 'all', label: 'All Groups' },
    { value: 'enabled', label: 'Enabled' },
    { value: 'disabled', label: 'Disabled' },
    { value: 'peer_group', label: 'Peer Group' },
    { value: 'trunk_group', label: 'Trunk Group' },
    { value: 'carrier_group', label: 'Carrier Group' },
    { value: 'teams_group', label: 'Teams Group' },
    { value: 'emergency_group', label: 'Emergency Group' },
  ];

  if (isLoading) {
    return <LoadingState />;
  }

  if (error) {
    return (
      <ErrorState
        title="Failed to load peer groups"
        message={error instanceof Error ? error.message : 'Unknown error'}
        onRetry={() => refetch()}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        title="Peer Groups"
        description="Manage peer groups for load balancing and routing"
        onRefresh={() => refetch()}
        isRefreshing={isFetching}
        primaryAction={{
          label: 'Create Group',
          icon: Plus,
          onClick: () => {
            setEditingGroup(null);
            setShowCreateModal(true);
          },
        }}
      />

      {/* Statistics Cards */}
      <StatsGrid>
        <StatCard
          label="Total Groups"
          value={stats.total}
          icon={Users}
          iconBgColor="bg-blue-100"
          iconColor="text-blue-600"
        />
        <StatCard
          label="Enabled"
          value={stats.enabled}
          icon={CheckCircle}
          iconBgColor="bg-green-100"
          iconColor="text-green-600"
          valueColor="text-green-600"
        />
        <StatCard
          label="Total Peers"
          value={stats.totalPeers}
          icon={Globe}
          iconBgColor="bg-purple-100"
          iconColor="text-purple-600"
          valueColor="text-purple-600"
        />
        <StatCard
          label="Carrier Groups"
          value={stats.carrierGroups}
          icon={Phone}
          iconBgColor="bg-green-100"
          iconColor="text-green-600"
          valueColor="text-green-600"
        />
      </StatsGrid>

      {/* Filters and Search */}
      <FilterBar
        searchPlaceholder="Search groups..."
        searchValue={searchQuery}
        onSearchChange={setSearchQuery}
        filterOptions={filterOptions}
        filterValue={filterType}
        onFilterChange={(value) => setFilterType(value as FilterType)}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
      />

      {/* Groups Display */}
      {sortedGroups.length === 0 ? (
        <EmptyState
          icon={Users}
          title="No peer groups found"
          description="Get started by creating a peer group."
          isFiltered={!!(searchQuery || filterType !== 'all')}
          action={{
            label: 'Create Peer Group',
            icon: Plus,
            onClick: () => setShowCreateModal(true),
          }}
        />
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {sortedGroups.map((group) => {
            const typeConfig = getTypeConfig(group.groupType as keyof typeof GROUP_TYPE_CONFIG);
            const TypeIcon = typeConfig.icon;

            return (
              <ItemCard
                key={group.id}
                icon={
                  <ItemCardIcon
                    icon={<TypeIcon className={`w-5 h-5 ${group.enabled ? 'text-blue-600' : 'text-gray-400'}`} />}
                    bgColor={group.enabled ? 'bg-blue-100' : 'bg-gray-100'}
                  />
                }
                title={group.name}
                subtitle={group.description || 'No description'}
                status={<StatusBadge status={enabledToStatus(group.enabled)} />}
                details={[
                  {
                    label: 'Type',
                    value: (
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${typeConfig.color}`}>
                        {typeConfig.label}
                      </span>
                    ),
                  },
                  {
                    label: 'Strategy',
                    value: (
                      <span className="flex items-center gap-1">
                        <Scale className="w-3 h-3" />
                        {STRATEGY_LABELS[group.loadBalancingStrategy] || group.loadBalancingStrategy}
                      </span>
                    ),
                  },
                  {
                    label: 'Selection',
                    value: SELECTION_MODE_LABELS[group.selectionMode] || group.selectionMode,
                  },
                  {
                    label: 'Members',
                    value: <span className="font-medium">{group.peerCount} peers</span>,
                  },
                ]}
                actions={
                  <ItemActions
                    variant="card"
                    actions={[
                      { kind: 'add', onClick: () => handleAddPeers(group) },
                      { kind: 'view', onClick: () => handleViewDetails(group) },
                      { kind: 'edit', onClick: () => handleEditGroup(group) },
                      {
                        kind: group.enabled ? 'disable' : 'enable',
                        onClick: () => handleToggleEnabled(group),
                        disabled: toggleMutation.isPending,
                      },
                      { kind: 'delete', onClick: () => handleDeleteGroup(group) },
                    ]}
                  />
                }
              />
            );
          })}
        </div>
      ) : (
        // List View
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Group</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Strategy</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Selection</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Members</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sortedGroups.map((group) => {
                const typeConfig = getTypeConfig(group.groupType as keyof typeof GROUP_TYPE_CONFIG);

                return (
                  <tr key={group.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-col">
                        <div className="text-sm font-medium text-gray-900">{group.name}</div>
                        {group.description && (
                          <div className="text-xs text-gray-500">{group.description}</div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${typeConfig.color}`}>
                        {typeConfig.label}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {STRATEGY_LABELS[group.loadBalancingStrategy] || group.loadBalancingStrategy}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {SELECTION_MODE_LABELS[group.selectionMode] || group.selectionMode}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {group.peerCount} peers
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <StatusBadge status={enabledToStatus(group.enabled)} />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <ItemActions
                        variant="table"
                        actions={[
                          { kind: 'add', title: 'Add peers', onClick: () => handleAddPeers(group) },
                          { kind: 'view', onClick: () => handleViewDetails(group) },
                          { kind: 'edit', onClick: () => handleEditGroup(group) },
                          {
                            kind: group.enabled ? 'disable' : 'enable',
                            onClick: () => handleToggleEnabled(group),
                            disabled: toggleMutation.isPending,
                          },
                          { kind: 'delete', onClick: () => handleDeleteGroup(group) },
                        ]}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Create/Edit Group Editor (tabbed modal) */}
      <PeerGroupEditor
        isOpen={showCreateModal}
        editingGroup={editingGroup}
        onClose={() => {
          setShowCreateModal(false);
          setEditingGroup(null);
        }}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['peer-groups'] });
          setShowCreateModal(false);
          setEditingGroup(null);
        }}
      />

      {/* Group Details Modal */}
      {showDetailsModal && selectedGroup && (
        <GroupDetailsModal
          group={selectedGroup}
          onClose={() => {
            setShowDetailsModal(false);
            setSelectedGroup(null);
          }}
          onAddPeers={() => {
            setShowDetailsModal(false);
            setShowAddPeersModal(true);
          }}
        />
      )}

      {/* Add Peers Modal */}
      {showAddPeersModal && selectedGroup && (
        <AddPeersModal
          group={selectedGroup}
          onClose={() => {
            setShowAddPeersModal(false);
            setSelectedGroup(null);
          }}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['peer-groups'] });
            queryClient.invalidateQueries({ queryKey: ['available-peers'] });
          }}
        />
      )}

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={showDeleteModal && !!groupToDelete}
        onClose={() => {
          setShowDeleteModal(false);
          setGroupToDelete(null);
        }}
        onConfirm={confirmDeleteGroup}
        title="Delete Peer Group"
        entityType="peer group"
        entityName={groupToDelete?.name || ''}
        additionalInfo={
          groupToDelete && (
            <>
              <p className="text-sm text-gray-600 mb-1 mt-2">Members:</p>
              <p className="text-sm text-gray-700">{groupToDelete.peerCount} peers (will be unassigned)</p>
            </>
          )
        }
        isDeleting={deleteMutation.isPending}
        warningMessage="This action cannot be undone. Peers in this group will be unassigned but not deleted."
      />
    </div>
  );
}

// ========================================
// GROUP DETAILS MODAL
// ========================================

interface GroupDetailsModalProps {
  group: PeerGroupWithPeers;
  onClose: () => void;
  onAddPeers: () => void;
}

const GroupDetailsModal: React.FC<GroupDetailsModalProps> = ({
  group,
  onClose,
  onAddPeers
}) => {
  const [activeTab, setActiveTab] = useState<'overview' | 'members'>('overview');
  const [editingPeerId, setEditingPeerId] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const removePeerMutation = useRemovePeerFromGroup();
  const updateMembershipMutation = useUpdatePeerMembership();

  const typeConfig = GROUP_TYPE_CONFIG[group.groupType as keyof typeof GROUP_TYPE_CONFIG] || GROUP_TYPE_CONFIG.peer_group;
  const TypeIcon = typeConfig.icon;

  const handleRemovePeer = async (peerId: string, peerName: string) => {
    if (window.confirm(`Remove "${peerName}" from this group?`)) {
      await removePeerMutation.mutateAsync(peerId);
      queryClient.invalidateQueries({ queryKey: ['peer-groups'] });
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-3xl w-full h-[80vh] flex flex-col">
        <div className="p-6 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${group.enabled ? 'bg-blue-100' : 'bg-gray-100'}`}>
                <TypeIcon className={`w-6 h-6 ${group.enabled ? 'text-blue-600' : 'text-gray-400'}`} />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900">{group.name}</h2>
                <p className="text-sm text-gray-600">{group.description || 'No description'}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Tabs */}
          <div className="mt-6">
            <nav className="flex space-x-8">
              {[
                { id: 'overview', label: 'Overview', icon: Eye },
                { id: 'members', label: `Members (${group.peerCount})`, icon: Users }
              ].map(({ id, label, icon: Icon }) => (
                <button
                  key={id}
                  onClick={() => setActiveTab(id as 'overview' | 'members')}
                  className={`flex items-center space-x-2 py-2 px-1 border-b-2 font-medium text-sm ${activeTab === id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{label}</span>
                </button>
              ))}
            </nav>
          </div>
        </div>

        <div className="p-6 overflow-y-auto flex-1">
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Quick Stats */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center space-x-3">
                    <div className={`p-2 rounded-lg ${group.enabled ? 'bg-green-100' : 'bg-gray-100'}`}>
                      {group.enabled ? (
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      ) : (
                        <XCircle className="w-5 h-5 text-gray-600" />
                      )}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-600">Status</p>
                      <p className="text-lg font-semibold text-gray-900">
                        {group.enabled ? 'Enabled' : 'Disabled'}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <Scale className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-600">Load Balancing</p>
                      <p className="text-lg font-semibold text-gray-900">
                        {STRATEGY_LABELS[group.loadBalancingStrategy] || group.loadBalancingStrategy}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-purple-100 rounded-lg">
                      <Users className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-600">Members</p>
                      <p className="text-lg font-semibold text-gray-900">{group.peerCount} peers</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Details */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-3">Configuration</h3>
                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Group Type</span>
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${typeConfig.color}`}>
                      {typeConfig.label}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Selection Mode</span>
                    <span className="text-sm text-gray-900">
                      {SELECTION_MODE_LABELS[group.selectionMode] || group.selectionMode}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Health Check</span>
                    <span className="text-sm text-gray-900">
                      {group.healthCheckEnabled ? 'Enabled' : 'Disabled'}
                    </span>
                  </div>
                  {group.maxConcurrentCalls && (
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Max Concurrent Calls</span>
                      <span className="text-sm text-gray-900">{group.maxConcurrentCalls}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Created</span>
                    <span className="text-sm text-gray-900">{new Date(group.createdAt).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Updated</span>
                    <span className="text-sm text-gray-900">{new Date(group.updatedAt).toLocaleString()}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'members' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-600">
                  {group.peerCount === 0 ? 'No peers assigned to this group' : `${group.peerCount} peer(s) in this group`}
                </p>
                <button
                  onClick={onAddPeers}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm text-blue-600 hover:bg-blue-50 rounded-lg"
                >
                  <UserPlus className="w-4 h-4" />
                  Add Peers
                </button>
              </div>

              {group.peers.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 rounded-lg">
                  <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No peers assigned</h3>
                  <p className="text-gray-600 mb-4">Add SIP peers to this group for load balancing.</p>
                  <button
                    onClick={onAddPeers}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors mx-auto"
                  >
                    <UserPlus className="w-4 h-4" />
                    Add Peers
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800">
                    Lower priority numbers are tried first. Weight only applies among peers in the same priority tier.
                  </div>
                  {group.peers.map((peer) => {
                    const PeerIcon = PEER_TYPE_ICONS[peer.type] || Globe;
                    return (
                      <PeerMemberRow
                        key={peer.id}
                        peer={peer}
                        PeerIcon={PeerIcon}
                        isEditing={editingPeerId === peer.id}
                        onEdit={() => setEditingPeerId(peer.id)}
                        onCancel={() => setEditingPeerId(null)}
                        onRemove={() => handleRemovePeer(peer.id, peer.name)}
                        removeDisabled={removePeerMutation.isPending}
                        saveDisabled={updateMembershipMutation.isPending}
                        onSave={async (values) => {
                          await updateMembershipMutation.mutateAsync({ peerId: peer.id, ...values });
                          setEditingPeerId(null);
                          queryClient.invalidateQueries({ queryKey: ['peer-groups'] });
                        }}
                      />
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="p-6 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

interface PeerMemberRowProps {
  peer: PeerMemberInfo;
  PeerIcon: React.ComponentType<{ className?: string }>;
  isEditing: boolean;
  onEdit: () => void;
  onCancel: () => void;
  onRemove: () => void;
  removeDisabled: boolean;
  saveDisabled: boolean;
  onSave: (values: { weight: number; priority: number; enabled: boolean }) => Promise<void>;
}

const PeerMemberRow: React.FC<PeerMemberRowProps> = ({
  peer,
  PeerIcon,
  isEditing,
  onEdit,
  onCancel,
  onRemove,
  removeDisabled,
  saveDisabled,
  onSave,
}) => {
  const [weight, setWeight] = useState(peer.groupWeight ?? 50);
  const [priority, setPriority] = useState(peer.groupPriority ?? 1);
  const [enabled, setEnabled] = useState(peer.groupEnabled !== false);

  useEffect(() => {
    setWeight(peer.groupWeight ?? 50);
    setPriority(peer.groupPriority ?? 1);
    setEnabled(peer.groupEnabled !== false);
  }, [peer.groupWeight, peer.groupPriority, peer.groupEnabled]);

  const handleCancel = () => {
    setWeight(peer.groupWeight ?? 50);
    setPriority(peer.groupPriority ?? 1);
    setEnabled(peer.groupEnabled !== false);
    onCancel();
  };

  return (
    <div className="rounded-lg bg-gray-50 p-4">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${peer.enabled ? 'bg-blue-100' : 'bg-gray-100'}`}>
            <PeerIcon className={`w-4 h-4 ${peer.enabled ? 'text-blue-600' : 'text-gray-400'}`} />
          </div>
          <div>
            <p className="font-medium text-gray-900">{peer.name}</p>
            {!isEditing && (
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <span className="capitalize">{peer.type}</span>
                <span>|</span>
                <span>Weight: {peer.groupWeight ?? 50}</span>
                <span>|</span>
                <span>Priority: {peer.groupPriority ?? 1}</span>
              </div>
            )}
          </div>
        </div>

        {!isEditing && (
          <div className="flex items-center gap-2">
            <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${peer.enabled && peer.groupEnabled !== false
                ? 'bg-green-100 text-green-800'
                : 'bg-gray-100 text-gray-800'
              }`}>
              {peer.enabled && peer.groupEnabled !== false ? 'Active' : 'Inactive'}
            </span>
            <button
              onClick={onEdit}
              className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"
              title="Edit group membership"
            >
              <Edit3 className="w-4 h-4" />
            </button>
            <button
              onClick={onRemove}
              disabled={removeDisabled}
              className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
              title="Remove from group"
            >
              <UserMinus className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      {isEditing && (
        <div className="mt-4 grid grid-cols-1 gap-3 border-t border-gray-200 pt-4 md:grid-cols-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Weight</label>
            <input
              type="number"
              min={0}
              max={100}
              value={weight}
              onChange={(event) => setWeight(Number(event.target.value))}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Priority</label>
            <input
              type="number"
              min={1}
              max={100}
              value={priority}
              onChange={(event) => setPriority(Number(event.target.value))}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <label className="flex items-center gap-2 self-end pb-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(event) => setEnabled(event.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            Enabled in group
          </label>
          <div className="flex items-end justify-end gap-2">
            <button
              type="button"
              onClick={handleCancel}
              className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => onSave({ weight, priority, enabled })}
              disabled={saveDisabled || weight < 0 || weight > 100 || priority < 1 || priority > 100}
              className="flex items-center gap-2 rounded-lg bg-blue-600 px-3 py-2 text-sm text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Save className="w-4 h-4" />
              Save
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ========================================
// ADD PEERS MODAL
// ========================================

interface AddPeersModalProps {
  group: PeerGroupWithPeers;
  onClose: () => void;
  onSuccess: () => void;
}

const AddPeersModal: React.FC<AddPeersModalProps> = ({
  group,
  onClose,
  onSuccess
}) => {
  const [selectedPeers, setSelectedPeers] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [weight, setWeight] = useState(50);
  const [priority, setPriority] = useState(1);

  const { data: availablePeers, isLoading } = useAvailablePeers();
  const addPeerMutation = useAddPeerToGroup();

  const filteredPeers = useMemo(() => {
    if (!availablePeers) return [];
    return availablePeers.filter(peer =>
      peer.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      peer.type.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [availablePeers, searchQuery]);

  const togglePeer = (peerId: string) => {
    setSelectedPeers(prev =>
      prev.includes(peerId)
        ? prev.filter(id => id !== peerId)
        : [...prev, peerId]
    );
  };

  const handleAddPeers = async () => {
    for (const peerId of selectedPeers) {
      await addPeerMutation.mutateAsync({
        peerId,
        groupId: group.id,
        weight,
        priority
      });
    }
    onSuccess();
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-lg w-full max-h-[80vh] flex flex-col">
        <div className="p-6 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Add Peers to Group</h2>
              <p className="text-sm text-gray-600 mt-1">
                Add peers to "{group.name}"
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-4 overflow-y-auto flex-1">
          {/* Search */}
          <div className="flex items-center gap-2">
            <Search className="w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search available peers..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Available Peers List */}
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-6 h-6 text-gray-400 animate-spin" />
            </div>
          ) : filteredPeers.length === 0 ? (
            <div className="text-center py-8 bg-gray-50 rounded-lg">
              <Users className="w-10 h-10 text-gray-400 mx-auto mb-2" />
              <p className="text-gray-600">
                {availablePeers?.length === 0
                  ? 'No available peers. Create new peers or remove existing peers from their groups first.'
                  : 'No peers match your search.'
                }
              </p>
            </div>
          ) : (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {filteredPeers.map(peer => {
                const PeerIcon = PEER_TYPE_ICONS[peer.type] || Globe;
                const isSelected = selectedPeers.includes(peer.id);

                return (
                  <div
                    key={peer.id}
                    onClick={() => togglePeer(peer.id)}
                    className={`flex items-center justify-between p-3 border rounded-lg cursor-pointer transition-colors ${isSelected
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:bg-gray-50'
                      }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`p-1.5 rounded-lg ${peer.enabled ? 'bg-blue-100' : 'bg-gray-100'}`}>
                        <PeerIcon className={`w-4 h-4 ${peer.enabled ? 'text-blue-600' : 'text-gray-400'}`} />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{peer.name}</p>
                        <p className="text-xs text-gray-500 capitalize">{peer.type}</p>
                      </div>
                    </div>
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => togglePeer(peer.id)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                  </div>
                );
              })}
            </div>
          )}

          {/* Weight and Priority */}
          {selectedPeers.length > 0 && (
            <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-200">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Initial Weight
                </label>
                <input
                  type="number"
                  value={weight}
                  onChange={(e) => setWeight(Number(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  min="0"
                  max="100"
                />
                <p className="text-xs text-gray-500 mt-1">0-100, higher = more traffic</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Initial Priority
                </label>
                <input
                  type="number"
                  value={priority}
                  onChange={(e) => setPriority(Number(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  min="1"
                  max="100"
                />
                <p className="text-xs text-gray-500 mt-1">1-100, lower = higher priority</p>
              </div>
            </div>
          )}
        </div>

        <div className="p-6 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-600">
              {selectedPeers.length} peer(s) selected
            </p>
            <div className="flex items-center gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleAddPeers}
                disabled={selectedPeers.length === 0 || addPeerMutation.isPending}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {addPeerMutation.isPending ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Adding...
                  </>
                ) : (
                  <>
                    <UserPlus className="w-4 h-4" />
                    Add Selected
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
